import { useEffect, useMemo, useState } from "react";
import z from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Select, Modal, NumberString } from "@/components";
import isEmpty from "lodash/isEmpty";
import {
  passwordValidation,
  phoneNumberOptionalValidation,
  REQUIRED_MESSAGE,
} from "../company/schema";
import { useShopStore } from "@/store/shopStore";
import { toastError, toastSuccess } from "@/utils/toastify";
import {
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_SUCCESS_MESSAGE,
  RoleEnum,
  RoleManagerExcute,
  StoreParams,
} from "@/types";
import { filterParams } from "@/utils/filterParams";
import { useSearchParams } from "react-router-dom";
import { useLoadingStore } from "@/store/loadingStore";
import { useGroupStore } from "@/store/groupStore";
import { useCompanyStore } from "@/store/companyStore";
import { KEYS_SEARCH_PARAMS } from "./CompanyDetail";
import { useAuth } from "@/contexts/AuthContext";
import { toHalfWidth } from "@/utils/formatCodeNumber";
import { ROLE_LABELS } from "@/utils";

type CreateStoreFormData = {
  name: string;
  group?: number;
  address?: string;
  role?: string;
  userName: string;
  saleOffice?: string;
  post?: string;
  department?: string;
  email: string;
  password: string;
  phone?: string;
  report_history?: string;
  store_inspection_history?: string;
};

const FIRST_PAGE = 1;
const LIMIT = 20;

const ROLE_OPTIONS_CREATE_STORE = [
  { label: ROLE_LABELS.store, value: RoleEnum.STORE_MANAGER },
];

const SPACE_LETTER = " ";
const schema = z.object({
  name: z
    .string(REQUIRED_MESSAGE)
    .trim()
    .min(1, REQUIRED_MESSAGE)
    .max(255, "会社名は半角255文字以内で入力してください。"),
  group: z.number().optional(),
  address: z
    .string(REQUIRED_MESSAGE)
    .max(255, "会社名は半角255文字以内で入力してください。")
    .optional(),
  role: z.string().optional(),
  userName: z
    .string(REQUIRED_MESSAGE)
    .trim()
    .min(1, REQUIRED_MESSAGE)
    .max(255, "会社名は半角255文字以内で入力してください。"),
  saleOffice: z
    .string(SPACE_LETTER)
    .max(255, "会社名は半角255文字以内で入力してください。")
    .optional(),
  post: z
    .string(SPACE_LETTER)
    .max(255, "会社名は半角255文字以内で入力してください。")
    .optional(),
  department: z
    .string(SPACE_LETTER)
    .max(255, "会社名は半角255文字以内で入力してください。")
    .optional(),
  email: z
    .string(REQUIRED_MESSAGE)
    .trim()
    .email("メールアドレスは無効な形式です。"),
  password: passwordValidation,
  phone: phoneNumberOptionalValidation,
  report_history: z
    .string()
    .trim()
    .max(1000, "報告履歴は半角1000文字以内で入力してください。")
    .optional(),
  store_inspection_history: z
    .string()
    .trim()
    .max(1000, "随時点検履歴は半角1000文字以内で入力してください。")
    .optional(),
});

interface Props {
  resetSearch?: () => void;
}

export function CreateStore({ resetSearch }: Props) {
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [loadingGroupsInCompany, setLoadingGroupsInCompany] = useState(false);

  const { setLoading } = useLoadingStore();
  const { groupsInCompany, fetchGroupsInCompany } = useGroupStore();
  const { createShop, fetchShopsInCompany, setShopsInCompany } = useShopStore();
  const { company, fetchMyStores } = useCompanyStore();
  const { role, user } = useAuth();

  const companyId = Number(company?.id);
  const page = groupsInCompany.pagination?.page || 1;
  const hasMore = false; // Update loading inscroll sang load hết;

  const storeDisplayName = useMemo(() => {
    return company?.store_display_name || "店舗";
  }, [company]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setError,
  } = useForm<CreateStoreFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      role: RoleEnum.STORE_MANAGER,
      group:
        role === RoleEnum.HEAD_OFFICE_USER
          ? groupsInCompany?.data?.[0]?.value
          : undefined,
    },
  });

  const loadOptions = async (page: number) => {
    if (loadingGroupsInCompany) return;
    setLoadingGroupsInCompany(true);
    await fetchGroupsInCompany(role || RoleEnum.HEAD_OFFICE_USER, {
      companyId: companyId,
      page: page,
      limit: LIMIT,
      isPagination: 0, // update loading inscroll sang load hết
    });
    setLoadingGroupsInCompany(false);
  };

  useEffect(() => {
    loadOptions(FIRST_PAGE);
  }, []);

  useEffect(() => {
    if (groupsInCompany?.data?.length && role === RoleEnum.HEAD_OFFICE_USER) {
      reset({
        role: RoleEnum.STORE_MANAGER,
        group: groupsInCompany.data[0].value,
      });
    }
  }, [groupsInCompany, role, reset]);

  const handleScrollToBottom = () => {
    if (hasMore) {
      const nextPage = page + 1;
      loadOptions(nextPage);
    }
  };

  const handleOpen = () => {
    if (role === RoleEnum.HEAD_OFFICE_USER && (user?.groups?.length || 0) < 1) {
      toastError(
        `あなたは${storeDisplayName}を作成する権限がありません。権限を付与するには、少なくとも1つの管理グループに所属する必要があります。管理者に連絡ください。`
      );
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const onSubmit = async (data: CreateStoreFormData) => {
    setLoading(true);
    try {
      const resp = await createShop(
        filterParams({
          name: data.name?.trim(),
          group_id: data.group || undefined,
          address: data.address?.trim() || "",
          role: data.role || RoleEnum.STORE_MANAGER,
          username: data.userName?.trim(),
          sale_office: data.saleOffice?.trim() || "",
          position: data.post?.trim() || "",
          department: data.department?.trim() || "",
          email: data.email?.trim(),
          password: data.password,
          company_id: companyId,
          phone: data.phone || "",
          report_history: data.report_history,
          store_inspection_history: data.store_inspection_history,
        }) as unknown as StoreParams
      );
      if (
        !searchParams.get(KEYS_SEARCH_PARAMS.SEARCH_STORE) &&
        companyId &&
        Number(searchParams.get(KEYS_SEARCH_PARAMS.PAGE_STORE) || 1) < 2
      ) {
        await fetchMyStores({});
      }
      resetSearch?.();
      handleClose();
      reset();
      setLoading(false);
      toastSuccess(resp?.message || DEFAULT_SUCCESS_MESSAGE);

      // update list shops in company detail for select
      const response = await fetchShopsInCompany({
        companyId: companyId,
        page: FIRST_PAGE,
        limit: LIMIT,
        isPagination: 0,
      });
      setShopsInCompany(
        response.data.map((item) => ({ label: item.label, value: item.key }))
      );
    } catch (error: any) {
      setLoading(false);
      toastError(error.message || DEFAULT_ERROR_MESSAGE);
      if (error?.error) {
        if (error.error === "name") {
          setError("name", { message: error.message });
        }
        if (error.error === "email") {
          setError("email", { message: error.message });
        }
        if (error.error === "address") {
          setError("address", { message: error.message });
        }
      }
    }
  };

  const disabledCreateStore =
    !companyId ||
    company?.status == "DISABLE" ||
    !RoleManagerExcute.STORE.CREATE[role || RoleEnum.OTHER];

  return (
    <>
      <Button
        buttonType="primary"
        onClick={handleOpen}
        disabled={disabledCreateStore}
      >
        {storeDisplayName}作成
      </Button>
      {/* new shop */}
      <Modal
        title={`${storeDisplayName}作成`}
        open={open}
        onOpenChange={handleClose}
      >
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={`${storeDisplayName}名`}
              placeholder={`${storeDisplayName}名を入力してください。`}
              required
              {...register("name")}
              containerClassName="col-span-2"
              error={errors.name?.message}
              maxLength={150}
            />
            <Controller
              control={control}
              name="group"
              defaultValue={
                role === RoleEnum.HEAD_OFFICE_USER
                  ? groupsInCompany?.data?.[0]?.value
                  : undefined
              }
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => {
                const selectedGroup = value
                  ? groupsInCompany.data.find((i) => i.value === +value) ||
                    undefined
                  : undefined;
                return (
                  <Select
                    label="グループ"
                    placeholder="グループ一覧"
                    value={selectedGroup}
                    onChange={(item) => {
                      onChange(item?.value);
                    }}
                    required={role === RoleEnum.HEAD_OFFICE_USER}
                    options={groupsInCompany.data}
                    className="col-span-2"
                    isLoading={loadingGroupsInCompany}
                    onMenuScrollToBottom={handleScrollToBottom}
                    error={error?.message ?? ""}
                  />
                );
              }}
            />
            <Input
              label="住所"
              placeholder="住所を入力してください。"
              {...register("address")}
              containerClassName="col-span-2"
            />
            <Controller
              control={control}
              name="role"
              render={({ field: { onChange } }) => (
                <Select
                  label="役割"
                  isDisabled
                  value={{
                    label: ROLE_LABELS.store,
                    value: RoleEnum.STORE_MANAGER,
                  }}
                  onChange={onChange}
                  options={ROLE_OPTIONS_CREATE_STORE}
                  className="col-span-2"
                />
              )}
            />
            <Input
              label="ユーザー名"
              placeholder="ユーザー名を入力してください。"
              required
              {...register("userName")}
              containerClassName="col-span-2"
              error={errors.userName?.message}
            />
            <Controller
              control={control}
              name="phone"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <NumberString
                  label="電話番号"
                  name="phone"
                  placeholder="電話番号を入力してください。"
                  containerClassName="col-span-2"
                  error={error?.message ?? ""}
                  value={value || ""}
                  onChange={onChange}
                  maxLength={255}
                  allowHyphen
                />
              )}
            />
            <Input
              label="役職"
              placeholder="役職を入力してください。"
              {...register("post")}
              error={errors.post?.message}
            />
            <Input
              label="部署"
              placeholder="部署を入力してください。"
              {...register("department")}
            />
            <Controller
              control={control}
              name="email"
              render={({ field: { onChange, value } }) => (
                <Input
                  label="メールアドレス"
                  name="email"
                  placeholder="メールアドレスを入力してください。"
                  required
                  containerClassName="col-span-2"
                  error={errors.email?.message}
                  maxLength={150}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, value } }) => (
                <Input
                  label="パスワード"
                  name="password"
                  placeholder="最低8文字、かつ少なくとも1文字のアルファベットを含む"
                  required
                  containerClassName="col-span-2"
                  error={errors.password?.message}
                  value={value}
                  maxLength={255}
                  onChange={onChange}
                  onBlur={() => {
                    onChange(toHalfWidth(value));
                  }}
                />
              )}
            />
            <Input
              label="申告履歴"
              placeholder="申告履歴を入力してください。"
              containerClassName="col-span-2"
              {...register("report_history")}
            />
            <Input
              label="随時点検履歴"
              placeholder="随時点検履歴を入力してください。"
              containerClassName="col-span-2"
              {...register("store_inspection_history")}
            />
          </div>
          <div className="flex justify-end gap-4 mt-4">
            <Button
              buttonType="text"
              className="!h-12"
              type="button"
              onClick={handleClose}
            >
              キャンセル
            </Button>
            <Button
              buttonType="primary"
              className="!h-12"
              type="submit"
              disabled={!isEmpty(errors)}
            >
              作成
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
